import { useState } from "react";
import { useT } from "@/lib/i18n";
import { postForm } from "@/lib/api";

export type FormField = {
  name: string;
  label: string;
  type?: "text" | "email" | "url" | "textarea" | "select";
  options?: string[];
};

const inputCls =
  "w-full border-b border-if-line bg-transparent py-3 text-sm text-if-text placeholder:text-if-dim/50 transition-colors duration-300 focus:border-if-gold focus:outline-none";

export const PendingForm = ({
  endpoint,
  fields,
  submitLabel,
  testId,
  active = false,
}: {
  endpoint: string;
  fields: FormField[];
  submitLabel: string;
  testId: string;
  active?: boolean;
}) => {
  const t = useT();
  const [reply, setReply] = useState<string | null>(null);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSending(true);
    setError(null);
    const payload = Object.fromEntries(
      new FormData(e.currentTarget).entries(),
    ) as Record<string, string>;
    const res = await postForm(endpoint, payload);
    setSending(false);
    if (res.ok && active) setSent(true);
    else if (active) setError(res.detail);
    else setReply(res.detail);
  };

  if (active && sent) {
    return (
      <div
        data-testid={`${testId}-success`}
        className="border border-if-gold/30 bg-if-surface/40 p-10 md:p-14"
      >
        <span className="inline-block h-1.5 w-1.5 rotate-45 bg-if-gold" />
        <p className="mt-6 font-serif text-3xl font-light tracking-tight text-if-text">
          {t.form.successTitle}
        </p>
        <p className="mt-4 max-w-md text-sm font-light leading-relaxed text-if-dim">
          {t.form.successBody}
        </p>
      </div>
    );
  }

  const REQUIRED = ["name", "email", "message"];
  const isReq = (f: FormField) => REQUIRED.includes(f.name);

  return (
    <form onSubmit={onSubmit} data-testid={testId} className="space-y-8">
      {!active && (
        <div
          data-testid={`${testId}-inactive-notice`}
          className="flex items-start gap-4 border border-if-line bg-if-surface p-5"
        >
          <span className="mt-0.5 shrink-0 border border-if-gold/50 px-2 py-0.5 font-sans text-[9px] uppercase tracking-[0.25em] text-if-gold">
            {t.form.inactiveBadge}
          </span>
          <p className="text-xs font-light leading-relaxed text-if-dim">
            {t.form.inactiveNotice}
          </p>
        </div>
      )}
      <input
        type="text"
        name="website"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        className="hidden"
      />
      {fields.map((f) => (
        <label key={f.name} className="block">
          <span className="mb-2 block text-[10px] uppercase tracking-[0.25em] text-if-dim">
            {f.label}
          </span>
          {f.type === "textarea" ? (
            <textarea
              name={f.name}
              required={isReq(f)}
              rows={4}
              data-testid={`${testId}-${f.name}-input`}
              className={`${inputCls} resize-none`}
            />
          ) : f.type === "select" ? (
            <select
              name={f.name}
              required={isReq(f)}
              defaultValue=""
              data-testid={`${testId}-${f.name}-input`}
              className={`${inputCls} bg-if-black`}
            >
              <option value="" disabled>
                —
              </option>
              {(f.options ?? []).map((o) => (
                <option key={o} value={o}>
                  {o}
                </option>
              ))}
            </select>
          ) : (
            <input
              name={f.name}
              type={f.type ?? "text"}
              required={isReq(f)}
              data-testid={`${testId}-${f.name}-input`}
              className={inputCls}
            />
          )}
        </label>
      ))}
      {active && error && (
        <p
          data-testid={`${testId}-error`}
          className="border-l border-red-900/60 pl-4 text-xs font-light leading-relaxed text-red-300/90"
        >
          {t.form.errorTitle} — {t.form.errorBody}
          <span className="mt-1 block font-sans text-[10px] text-if-dim/70">
            {error}
          </span>
        </p>
      )}
      <button
        type="submit"
        disabled={sending}
        data-testid={`${testId}-submit`}
        className="bg-if-gold px-10 py-3.5 text-[11px] font-semibold uppercase tracking-[0.25em] text-if-black transition-colors duration-300 hover:bg-if-golddeep disabled:opacity-60"
      >
        {sending ? t.form.sending : submitLabel}
      </button>
      {!active && reply !== null && (
        <p
          data-testid={`${testId}-server-reply`}
          className="border-l border-if-gold/40 pl-4 font-sans text-[11px] leading-relaxed text-if-dim"
        >
          {t.form.serverReply}: {reply}
        </p>
      )}
    </form>
  );
};
